import type { CellContext } from "@tanstack/react-table";
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import formatFinancialValue from "components/company/financials/formatFinancialValue";
import { ScrollArea } from "components/ui/ScrollArea";
import classNames from "components/ui/classNames";
import numeral from "numeral";
import { useIntl } from "react-intl";
import type { IAmericanCompanyTotalCompensationByYear, IAmericanReitTotalCompensationByYear } from "types/graphqlTypes";

interface AmericanTotalCompensationsProps {
  totalCompensations: (IAmericanCompanyTotalCompensationByYear | IAmericanReitTotalCompensationByYear)[];
  isREIT: boolean;
}

export default function AmericanTotalCompensations({ totalCompensations, isREIT }: AmericanTotalCompensationsProps) {
  const intl = useIntl();

  const columnHelper = createColumnHelper<
    IAmericanCompanyTotalCompensationByYear | IAmericanReitTotalCompensationByYear
  >();

  const columns = [
    columnHelper.accessor("fiscalYear", {
      header: "Year",
      meta: {
        header: {
          className: "border-r border-slate-500 bg-slate-600 text-sm font-semibold text-white",
        },
        cell: {
          className:
            "font-monospace font-medium text-sm border-l-0 border-t border-r border-gray-500 text-center text-white bg-slate-600",
        },
      },
    }),
    columnHelper.accessor("totalExecutiveCompensation", {
      header: intl.formatMessage({
        description: "Remuneração dos Administradores",
        defaultMessage: "Diretoria",
        id: "kGjrtV",
      }),
      cell: formatCellValue,
      meta: {
        header: {
          tooltip: intl.formatMessage({
            description: "Remuneração dos Administradores",
            defaultMessage: "Escala: Em dólar",
            id: "Y1XisT",
          }),
          className: "border-r border-slate-200 bg-slate-100 text-sm font-semibold text-slate-600",
        },
        cell: {
          className:
            "font-monospace font-medium text-sm border border-l-0 border-b-0 text-slate-600 text-center border-slate-200",
        },
      },
    }),
    columnHelper.accessor("totalBoardOfDirectorsCompensation", {
      header: intl.formatMessage({
        description: "Remuneração dos Administradores",
        defaultMessage: "Conselho de Administração",
        id: "+syG2y",
      }),
      cell: formatCellValue,
      meta: {
        header: {
          tooltip: intl.formatMessage({
            description: "Remuneração dos Administradores",
            defaultMessage: "Escala: Em dólar",
            id: "Y1XisT",
          }),
          className: "border-r border-slate-200 bg-slate-100 text-sm font-semibold text-slate-600",
        },
        cell: {
          className:
            "font-monospace font-medium text-sm border border-l-0 border-b-0 text-slate-600 text-center border-slate-200",
        },
      },
    }),
    columnHelper.accessor("totalCompensation", {
      header: intl.formatMessage({
        description: "Remuneração dos Administradores",
        defaultMessage: "Total",
        id: "KKoQ7p",
      }),
      cell: formatCellValue,
      meta: {
        header: {
          tooltip: intl.formatMessage({
            description: "Remuneração dos Administradores",
            defaultMessage: "Escala: Em dólar",
            id: "Y1XisT",
          }),
          className: "bg-sky-100 text-sm font-semibold text-blue-600",
        },
        cell: {
          className:
            "font-monospace font-medium text-sm border border-l-0 border-b-0 text-blue-600 text-center border-slate-200",
        },
      },
    }),

    // Conditional Columns
    columnHelper.accessor("netIncome", {
      header: intl.formatMessage({
        description: "Remuneração dos Administradores",
        defaultMessage: "Lucro Líquido",
        id: "ZWn5dZ",
      }),
      cell: formatFinancialCellValue,
      meta: {
        isHidden: isREIT,
        header: {
          tooltip: intl.formatMessage({
            description: "Remuneração dos Administradores",
            defaultMessage: "Escala: Em milhões",
            id: "xtG8dl",
          }),
          className: "border-l border-green-200 bg-[#F1FFF3] text-sm font-semibold tracking-wide text-[#2f8132]",
        },
        cell: {
          className: (props: CellContext<IAmericanCompanyTotalCompensationByYear, number>) => {
            return classNames(
              "border border-b-0 border-l-0 border-slate-200 text-center font-monospace text-sm font-medium",
              {
                "text-[#3ea383]": props.row.original.netIncome >= 0,
                "text-[#c74343]": props.row.original.netIncome < 0,
              },
            );
          },
        },
      },
    }),
    columnHelper.accessor("percentOfNetIncome", {
      header: intl.formatMessage({
        description: "Remuneração dos Administradores",
        defaultMessage: "% Lucro",
        id: "HiAJXj",
      }),
      cell: (props) =>
        formatPercentageOfNetIncome(props as CellContext<IAmericanCompanyTotalCompensationByYear, number>),
      meta: {
        isHidden: isREIT,
        header: {
          className:
            "border-l text-sm font-semibold tracking-wide bg-[#faf4e5] text-[#997d2e] border-b border-slate-200",
        },
        cell: {
          className: getClassNameForPercentageOfNetIncome,
        },
      },
    }),
    columnHelper.accessor("fundsFromOperations", {
      header: "FFO",
      cell: formatFinancialCellValue,
      meta: {
        isHidden: !isREIT,
        header: {
          tooltip: intl.formatMessage({
            description: "Remuneração dos Administradores",
            defaultMessage: "Escala: Em milhões",
            id: "xtG8dl",
          }),
          className: "border-l border-green-200 bg-[#F1FFF3] text-sm font-semibold tracking-wide text-[#2f8132]",
        },
        cell: {
          className: (props: CellContext<IAmericanReitTotalCompensationByYear, number>) => {
            return classNames("border border-b-0 border-l-0 font-monospace text-sm font-medium sm:border-slate-200", {
              "text-[#2f8132]": props.row.original.fundsFromOperations > 0,
              "text-red-500": props.row.original.fundsFromOperations < 0,
            });
          },
        },
      },
    }),
    columnHelper.accessor("percentOfFundsFromOperations", {
      header: "% FFO",
      cell: (props) => formatPercentageOfFFO(props as CellContext<IAmericanReitTotalCompensationByYear, number>),
      meta: {
        isHidden: !isREIT,
        header: {
          className:
            "border-l text-sm font-semibold tracking-wide bg-[#faf4e5] text-[#997d2e] border-b border-slate-200",
        },
        cell: {
          className: getClassNameForPercentageOfFFO,
        },
      },
    }),

    columnHelper.accessor("revenue", {
      header: intl.formatMessage({
        description: "Remuneração dos Administradores",
        defaultMessage: "Receita",
        id: "bid7Xd",
      }),
      cell: formatFinancialCellValue,
      meta: {
        header: {
          tooltip: intl.formatMessage({
            description: "Remuneração dos Administradores",
            defaultMessage: "Escala: Em milhões",
            id: "xtG8dl",
          }),
          className:
            "border-l bg-[#F1FFF3] text-sm font-semibold tracking-wide text-[#2f8132] border-b border-slate-200",
        },
        cell: {
          className: "font-monospace font-medium text-sm border border-b-0 text-[#57ae5b] text-center border-slate-200",
        },
      },
    }),
    columnHelper.accessor("percentageOfRevenue", {
      header: intl.formatMessage({
        description: "Remuneração dos Administradores",
        defaultMessage: "% Receita",
        id: "0yvHj/",
      }),
      cell: formatPercentageOfRevenue,
      meta: {
        header: {
          className:
            "border-l text-sm font-semibold tracking-wide bg-[#faf4e5] text-[#997d2e] border-b border-slate-200",
        },
        cell: {
          className: getClassNameForPercentageOfRevenue,
        },
      },
    }),
  ];

  const table = useReactTable({
    data: totalCompensations,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <ScrollArea type="auto" orientation="horizontal" className="relative h-full w-full">
      <div className="pb-4 lg:w-full lg:p-0">
        <div
          className={classNames("relative overflow-auto rounded-md border border-slate-200", "dark:border-slate-950")}
        >
          <table className="w-full divide-y divide-slate-200 dark:divide-slate-700">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr
                  key={headerGroup.id}
                  className="select-none divide-x divide-slate-200 dark:divide-slate-700 [&>th]:whitespace-nowrap"
                >
                  {headerGroup.headers
                    .filter((header) => !header.column.columnDef.meta?.isHidden)
                    .map((header) => {
                      return (
                        <th
                          key={header.id}
                          className={classNames(
                            "px-3 py-1 text-sm font-semibold",
                            header.column.columnDef.meta?.header?.className,
                          )}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </th>
                      );
                    })}
                </tr>
              ))}
            </thead>
            <tbody
              className={classNames(
                "[&>tr>td]:text-center",
                "[&>tr>td:not(:first-of-type)]:border-x",
                "[&>tr>td:not(:first-of-type)]:border-[#dbe2eb]",
                "[&>tr:hover]:bg-slate-50",
              )}
            >
              {table.getRowModel().rows.map((row) => {
                return (
                  <tr key={row.id} className="group divide-x divide-gray-200 dark:divide-slate-700">
                    {row
                      .getVisibleCells()
                      .filter((cell) => !cell.column.columnDef.meta?.isHidden)
                      .map((cell) => {
                        const cellClassNames =
                          typeof cell.column.columnDef.meta?.cell?.className === "function"
                            ? cell.column.columnDef.meta?.cell?.className(cell)
                            : cell.column.columnDef.meta?.cell?.className;

                        return (
                          <td
                            key={cell.id}
                            className={classNames("px-3 py-1.5 font-mono text-sm font-semibold", cellClassNames)}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        );
                      })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </ScrollArea>
  );
}

function formatCellValue(
  props: CellContext<IAmericanCompanyTotalCompensationByYear | IAmericanReitTotalCompensationByYear, number>,
) {
  return numeral(props.getValue()).format("0,0").replace(/,/g, ".");
}

function formatFinancialCellValue(
  props: CellContext<IAmericanCompanyTotalCompensationByYear | IAmericanReitTotalCompensationByYear, number>,
) {
  return formatFinancialValue(props.getValue());
}

function formatPercentageOfNetIncome(props: CellContext<IAmericanCompanyTotalCompensationByYear, number>) {
  const value = props.row.original.netIncome === 0 ? 0 : props.getValue() * 100;
  let formattedRatio = `${(value || 0).toFixed(2).replace(".", ",")}%`;

  if ((value || 0) >= 10) {
    formattedRatio = `${Math.round(value || 0)}%`;
  }

  if ((value || 0) < 0 || props.row.original.netIncome < 0) {
    formattedRatio = "P";
  }

  return formattedRatio;
}

function formatPercentageOfFFO(props: CellContext<IAmericanReitTotalCompensationByYear, number>) {
  const value = props.row.original.fundsFromOperations === 0 ? 0 : props.getValue() * 100;
  let formattedRatio = `${(value || 0).toFixed(2).replace(".", ",")}%`;

  if ((value || 0) >= 10) {
    formattedRatio = `${Math.round(value || 0)}%`;
  }

  if ((value || 0) < 0 || props.row.original.fundsFromOperations < 0) {
    formattedRatio = "P";
  }

  return formattedRatio;
}

function formatPercentageOfRevenue(
  props: CellContext<IAmericanCompanyTotalCompensationByYear | IAmericanReitTotalCompensationByYear, number>,
) {
  const value = props.row.original.revenue === 0 ? 0 : props.getValue() * 100;

  return `${(value || 0) >= 10 ? Math.round(value || 0) : (value || 0).toFixed(2).replace(".", ",")}%`;
}

function getClassNameForPercentageOfNetIncome(props: CellContext<IAmericanCompanyTotalCompensationByYear, number>) {
  if (props.row.original.netIncome < 0) {
    return "bg-[#FCF1F1] text-[#c74343] text-sm text-center font-medium font-monospace border-t border-slate-200";
  }

  const value = props.row.original.netIncome === 0 ? 0 : props.getValue() * 100;
  if (value >= 10) {
    return "bg-[#FCF1F1] text-[#c74343] text-sm text-center font-medium font-monospace border-t border-slate-200";
  }

  if (value < 10 && value > 3) {
    return "bg-[#fffbea] text-[#7e6626] text-sm text-center font-medium font-monospace border-t border-slate-200";
  }

  if (value <= 3) {
    return "bg-[#F1FFF3] text-[#2f8132] text-sm text-center font-medium font-monospace border-t border-slate-200";
  }

  return null;
}

function getClassNameForPercentageOfFFO(props: CellContext<IAmericanReitTotalCompensationByYear, number>) {
  if (props.row.original.fundsFromOperations < 0) {
    return "bg-[#FCF1F1] text-[#c74343] text-sm text-center font-medium font-monospace border-t border-slate-200";
  }

  const value = props.row.original.fundsFromOperations === 0 ? 0 : props.getValue() * 100;
  if (value >= 10) {
    return "bg-[#FCF1F1] text-[#c74343] text-sm text-center font-medium font-monospace border-t border-slate-200";
  }

  if (value < 10 && value > 3) {
    return "bg-[#fffbea] text-[#7e6626] text-sm text-center font-medium font-monospace border-t border-slate-200";
  }

  if (value <= 3) {
    return "bg-[#F1FFF3] text-[#2f8132] text-sm text-center font-medium font-monospace border-t border-slate-200";
  }

  return null;
}

function getClassNameForPercentageOfRevenue(
  props: CellContext<IAmericanCompanyTotalCompensationByYear | IAmericanReitTotalCompensationByYear, number>,
) {
  const value = props.row.original.revenue === 0 ? 0 : props.getValue() * 100;

  if (value >= 2) {
    return "bg-[#FCF1F1] text-[#c74343] text-sm text-center font-medium font-monospace border-t border-slate-200";
  }

  if (value < 2 && value > 0.5) {
    return "bg-[#fffbea] text-[#7e6626] text-sm text-center font-medium font-monospace border-t border-slate-200";
  }

  if (value <= 0.5) {
    return "bg-[#F1FFF3] text-[#2f8132] text-sm text-center font-medium font-monospace border-t border-slate-200";
  }

  return null;
}
